import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const platformColors = {
  dark: [
    'rgba(96, 165, 250, 0.9)',   // Blue - Web
    'rgba(192, 132, 252, 0.9)',  // Purple - iOS
    'rgba(52, 211, 153, 0.9)',   // Green - Android
    'rgba(248, 113, 113, 0.9)',  // Red - Other
  ],
  light: [
    'rgba(37, 99, 235, 0.8)',    // Blue - Web
    'rgba(147, 51, 234, 0.8)',   // Purple - iOS
    'rgba(5, 150, 105, 0.8)',    // Green - Android
    'rgba(220, 38, 38, 0.8)',    // Red - Other
  ]
};

const PlatformChart = ({ data, isDarkMode, periodLabel = 'All Platforms' }) => {
  const textColor = isDarkMode ? '#e5e7eb' : '#333333';
  const cardBg = isDarkMode ? '#1f2937' : '#ffffff';
  const colors = isDarkMode ? platformColors.dark : platformColors.light;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatPlatformLabel = (platform) => {
    if (!platform) return platform;
    const lower = platform.toLowerCase();
    if (lower === 'booktime') return 'App';
    if (lower === 'web') return 'Web';
    if (lower === 'app') return 'App';
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const chartData = {
    labels: data.map(item => formatPlatformLabel(item.platform)),
    datasets: [
      {
        label: 'Total Views',
        data: data.map(item => item.totalViews),
        backgroundColor: data.map((_, i) => colors[i % colors.length]),
        borderColor: cardBg,
        borderWidth: 3,
        hoverBorderWidth: 4,
        hoverBorderColor: cardBg,
        hoverOffset: 15
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          color: textColor,
          font: {
            size: 12,
            family: "'Inter', sans-serif"
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#3491f7',
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            const item = data[context.dataIndex];
            return [
              `Views: ${value.toLocaleString()} (${percentage}%)`,
              `Users: ${item.activeUsers.toLocaleString()}`
            ];
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>Platform Usage</h3>
        <span className="chart-badge">{periodLabel}</span>
      </div>
      <div className="chart-wrapper">
        {data.length > 0 ? (
          <Doughnut data={chartData} options={options} />
        ) : (
          <p style={{ color: textColor, textAlign: 'center', paddingTop: '40px' }}>No data available</p>
        )}
      </div>
    </div>
  );
};

export default PlatformChart;
